import { useTranslation } from "react-i18next";
import { FunnelStepContainer } from "v2/components/funnel/ui-elements/FunnelStepContainer";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "v2/components/funnel/ui-elements/FunnelStepControls";
import { FunnelStepSection } from "v2/components/funnel/ui-elements/FunnelStepSection";
import { ControlledRadioGroup, useForm, useFormHandlers } from "v2/modules/form";
import { useFunnel } from "@airhelp/funnel";
import {
  getArrivalAirport,
  updateClaimDetails,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import {
  CLAIM_TYPES_WITH_TRANSLATIONS,
  SUPPORTED_CLAIM_TYPES,
  SUPPORTED_DELAYS,
  SUPPORTED_INFORMED_BEFOREHAND,
} from "v2/settings/constants";
import { z } from "zod";
import { useEffect, useRef, useState } from "react";
import scrollToRef from "utils/scrollToRef";
import { OverbookBlockModal } from "v2/components/funnel/modals/OverbookBlockModal";
import { useCreateClaimEnquiry } from "../../../modules/hooks/useCreateClaimEnquiry";
import { useDelayOptions } from "../../../modules/hooks/useDelayOptions";
import { useInformedBeforehandOptions } from "../../../modules/hooks/useInformedBeforehandOptions";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { ControlledSelect } from "../../../modules/form/ControlledSelect";
import { ControlledCheckBox } from "v2/modules/form/ControlledCheckbox";
import { TFunction } from "i18next";

export function getSelectOptions(
  options: Readonly<string[]>,
  translationOptions: Record<string, string>,
  t: TFunction,
) {
  return options.map((option: string) => ({
    label: t(translationOptions[option]),
    value: option,
  }));
}

const voluntarilySurrenderedOptions = [true, false];

const defaultStepSchema = z.object({
  claimType: z.enum(SUPPORTED_CLAIM_TYPES).nullable(),
  totalDelay: z.enum(SUPPORTED_DELAYS).nullable(),
  neverArrived: z.boolean().nullable(),
  informedBeforehand: z.enum(SUPPORTED_INFORMED_BEFOREHAND).nullable(),
  voluntarilySurrendered: z.boolean().nullable(),
});

export function DelayInformationStep() {
  const { t } = useTranslation();
  const funnel = useFunnel();
  const { itineraryFlights, claimDetails, flightDetails } = usePreEligibilityDataSnapshot();
  const totalDelayRef = useRef(null);
  const lastSectionRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { createClaimEnquiry } = useCreateClaimEnquiry();
  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });
  const [isValid, setIsValid] = useState(false);

  const arrivalAirport = getArrivalAirport();

  const isSimplifiedFlow = claimDetails.prefilled;
  const stepSchema = z
    .object({
      claimType: z.enum(SUPPORTED_CLAIM_TYPES),
      totalDelay: z.enum(SUPPORTED_DELAYS).nullable(),
      neverArrived: z.boolean().optional().nullable(),
      informedBeforehand: z.enum(SUPPORTED_INFORMED_BEFOREHAND).optional().nullable(),
      voluntarilySurrendered: z.boolean().optional().nullable(),
    })
    .refine(
      ({ claimType, totalDelay, neverArrived, informedBeforehand, voluntarilySurrendered }) => {
        const isDelayValueProvided = neverArrived || !!totalDelay;
        if (
          claimType === "delayed" &&
          isDelayValueProvided &&
          (!isSimplifiedFlow || informedBeforehand) &&
          (!isBrazil || informedBeforehand)
        ) {
          return true;
        }
        if (claimType === "cancelled" && isDelayValueProvided && informedBeforehand) {
          return true;
        }
        if (
          claimType === "overbooked" &&
          isDelayValueProvided &&
          typeof voluntarilySurrendered === "boolean"
        ) {
          return true;
        }

        return false;
      },
      {
        message: "Missing required field based on claimType",
      },
    );
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      claimType: claimDetails.claimType,
      totalDelay: claimDetails.totalDelay,
      neverArrived: claimDetails.totalDelay === "never" || flightDetails.isFutureFlight,
      informedBeforehand: claimDetails.informedBeforehand,
      voluntarilySurrendered: claimDetails.voluntarilySurrendered,
    },
  });

  function resetFields(fields: ("totalDelay" | "voluntarilySurrendered" | "informedBeforehand")[]) {
    fields.forEach((field) => setValue(field, null, { shouldValidate: true }));
  }

  const claimType = watch("claimType");
  const totalDelay = watch("totalDelay");
  const informedBeforehand = watch("informedBeforehand");
  const voluntarilySurrendered = watch("voluntarilySurrendered");
  const neverArrived = watch("neverArrived");

  useEffect(() => {
    setIsValid(
      stepSchema.safeParse({
        claimType,
        totalDelay,
        neverArrived,
        informedBeforehand,
        voluntarilySurrendered,
      }).success,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimType, totalDelay, neverArrived, informedBeforehand, voluntarilySurrendered]);

  const { options: totalDelayOptions, optionsWithTranslations: totalDelayOptionsWithTranslations } =
    useDelayOptions({
      claimType,
      potentialRegulation: claimDetails.potentialRegulation,
    });

  const {
    options: informedBeforehandOptions,
    optionsWithTranslations: informedBeforehandOptionsWithTranslations,
  } = useInformedBeforehandOptions({ potentialRegulation: claimDetails.potentialRegulation });

  const isFutureFlight = flightDetails.isFutureFlight;
  const isClaimTypeSelected = !!claimType;
  const isDelaySelected = !!totalDelay || neverArrived || isFutureFlight;
  const isOverbooked = claimType === "overbooked";
  const isCancelled = claimType === "cancelled";
  const isBrazil = claimDetails.potentialRegulation === "brazil";
  const isTrShy = claimDetails.potentialRegulation === "tr_shy";
  const isSaudi = claimDetails.potentialRegulation === "saudi_arabia";

  const options = isFutureFlight
    ? SUPPORTED_CLAIM_TYPES.filter((type) => type !== "delayed")
    : SUPPORTED_CLAIM_TYPES;

  const showClaimTypeSection = !isSimplifiedFlow;
  const showDelaySection = isClaimTypeSelected && !isFutureFlight && !isSimplifiedFlow;
  const showInformedBeforehandSection =
    (isCancelled || (isBrazil && !isOverbooked)) && isDelaySelected;
  const showOverbookedSection = isOverbooked && isDelaySelected;

  const translationPrefix = () => {
    switch (true) {
      case isBrazil:
        return "steps.informed_beforehand.brazil";
      case isTrShy:
        return "steps.informed_beforehand.tr_shy";
      case isSaudi:
        return "steps.informed_beforehand.saudi_arabia";
      default:
        return "steps.informed_beforehand";
    }
  };

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    async onSubmit({ informedBeforehand, voluntarilySurrendered }) {
      const updatedClaimDetails = updateClaimDetails({
        claimType,
        totalDelay: neverArrived ? "never" : totalDelay,
        informedBeforehand,
        voluntarilySurrendered,
      });

      setIsModalOpen(false);

      if (funnel.currentStep?.meta?.claimShouldBeSubmitted) {
        const rawClaimEnquiry = await createClaimEnquiry({
          itineraryFlights,
          claimDetails: updatedClaimDetails,
          experiment: isFutureFlight ? "future_flight" : undefined,
        });
        trackClaimProcess({
          funnelStep: funnel.currentStep?.name,
          isEligible: rawClaimEnquiry?.claim_valid,
        });
        trackEnquiryStepComplete();
      } else {
        trackClaimProcess({ funnelStep: funnel.currentStep?.name });
        trackEnquiryStepComplete();
        funnel.stepForward();
      }
    },
  });

  const submitFunction = isOverbooked ? () => setIsModalOpen(true) : handleSubmit(onSubmit);

  return (
    <FunnelStepContainer data-testid={"DelayInformationStep"}>
      {showClaimTypeSection && (
        <FunnelStepSection title={t("steps.claim_type.text")}>
          <ControlledRadioGroup
            name={"claimType"}
            control={control}
            options={options}
            renderOption={(option) => t(CLAIM_TYPES_WITH_TRANSLATIONS[option])}
            onChange={() => {
              scrollToRef(totalDelayRef);
              resetFields(["totalDelay", "informedBeforehand", "voluntarilySurrendered"]);
            }}
          />
        </FunnelStepSection>
      )}

      <span ref={totalDelayRef} />
      {showDelaySection && (
        <FunnelStepSection
          title={t("steps.total_delay.text", {
            city: arrivalAirport?.city,
            iata: arrivalAirport?.iata,
          })}
        >
          {isSaudi ? (
            <>
              <ControlledSelect
                isDisabled={neverArrived}
                dataTestId="totalDelay"
                name="totalDelay"
                handleChange={() => {
                  scrollToRef(lastSectionRef);
                  resetFields(["informedBeforehand", "voluntarilySurrendered"]);
                }}
                control={control}
                options={getSelectOptions(
                  totalDelayOptions,
                  totalDelayOptionsWithTranslations,
                  t,
                ).filter((option) => option.value !== "never")}
              />
              <ControlledCheckBox
                name="neverArrived"
                data-testId="neverArrived"
                control={control}
                mt="6"
                handleChange={() => {
                  resetFields(["totalDelay", "informedBeforehand", "voluntarilySurrendered"]);
                  if (neverArrived) {
                    scrollToRef(lastSectionRef);
                  }
                }}
              >
                {t("steps.total_delay.saudi_arabia.never_arrived")}
              </ControlledCheckBox>
            </>
          ) : (
            <ControlledRadioGroup
              name={"totalDelay"}
              control={control}
              options={totalDelayOptions}
              // @ts-ignore
              renderOption={(option) => t(totalDelayOptionsWithTranslations[option])}
              onChange={() => {
                scrollToRef(lastSectionRef);
                resetFields(["informedBeforehand", "voluntarilySurrendered"]);
              }}
            />
          )}
        </FunnelStepSection>
      )}

      <span ref={lastSectionRef} />

      {showInformedBeforehandSection && (
        <FunnelStepSection
          data-testid="InformedBeforehandDays"
          title={t(`${translationPrefix()}.text`)}
        >
          {isSaudi ? (
            <ControlledSelect
              name="informedBeforehand"
              dataTestId="informedBeforehand"
              control={control}
              options={getSelectOptions(
                informedBeforehandOptions,
                informedBeforehandOptionsWithTranslations,
                t,
              )}
            />
          ) : (
            <ControlledRadioGroup
              name={"informedBeforehand"}
              control={control}
              options={informedBeforehandOptions}
              // @ts-ignore
              renderOption={(option) => t(informedBeforehandOptionsWithTranslations[option])}
            />
          )}
        </FunnelStepSection>
      )}

      {showOverbookedSection && (
        <FunnelStepSection title={t("steps.volunteer.text")}>
          <ControlledRadioGroup
            name={"voluntarilySurrendered"}
            control={control}
            options={voluntarilySurrenderedOptions}
            renderOption={(option) => t(`steps.volunteer.${option}`)}
          />
        </FunnelStepSection>
      )}

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={submitFunction}
        />
      </FunnelStepControls>
      <OverbookBlockModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleSubmit(onSubmit)}
      />
    </FunnelStepContainer>
  );
}
